import React, { useState, useEffect, useRef } from 'react';
import { Headphones, X, Send } from 'lucide-react';
import { toast } from 'sonner';
import { contactAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

/**
 * FloatingContactFAB (iter59) — A1 variant.
 *
 *  - 56px round FAB pinned bottom-right (above mobile bottom-nav).
 *  - Clicking opens a compact form card (Name, Email, Phone, Subject, Message).
 *  - Auto-fills Name/Email from AuthContext when the user is logged in.
 *  - Submits to POST /api/contact, then closes & toasts a confirmation.
 *
 * Color tokens follow GenzoX brand: orange #ff8500 (primary) / blue #1800ad (hover).
 */

const SUBJECT_OPTIONS = [
  'General Enquiry',
  'Account / Login Issue',
  'Listing Issue',
  'Payment / Subscription',
  'Dealer Plan Enquiry',
  'Report Abuse',
  'Feedback / Suggestion',
  'Other',
];

const FloatingContactFAB = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const firstInputRef = useRef(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', subject: 'General Enquiry', message: '',
  });

  // Pre-fill from logged-in profile each time the modal opens.
  useEffect(() => {
    if (!open) return;
    setForm((prev) => ({
      ...prev,
      name: prev.name || user?.name || '',
      email: prev.email || user?.email || '',
      phone: prev.phone || user?.phone || '',
    }));
    // Focus the first empty field after the slide-in finishes
    setTimeout(() => { firstInputRef.current?.focus(); }, 250);
  }, [open, user]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const handleChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const name = form.name.trim();
    const email = form.email.trim();
    const msg = form.message.trim();
    if (name.length < 2) { toast.error('Please enter your name'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Please enter a valid email'); return; }
    if (msg.length < 10) { toast.error('Message must be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      await contactAPI.submit({
        name, email, phone: form.phone.trim() || undefined,
        subject: form.subject, message: msg,
      });
      toast.success("Thanks! We'll reply within 24 hours.");
      setOpen(false);
      setForm((f) => ({ ...f, message: '' }));  // keep name/email, clear message body
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* The FAB itself */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Contact us"
        data-testid="floating-contact-fab"
        className="fixed bottom-[68px] right-3 z-40 w-12 h-12 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-[#ff8500]/30"
        style={{ background: 'linear-gradient(135deg, #ff8500 0%, #ff6a00 100%)' }}
      >
        <Headphones size={22} />
      </button>

      {/* Backdrop + slide-up panel */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end" data-testid="floating-contact-modal">
          <button
            type="button"
            aria-label="Close contact form"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
          />
          <form
            onSubmit={handleSubmit}
            className="relative w-full sm:w-[380px] sm:mr-6 sm:mb-24 max-h-[92vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl border border-[#1800ad]/10 animate-[slideup_220ms_ease-out]"
            style={{ animation: 'fadeup .22s ease-out' }}
          >
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 className="text-base font-bold text-[#0A0B10]">Contact Us</h2>
                <p className="text-[11px] text-[#6B7280]">You will receive a reply from GenzoX within 24h</p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="floating-contact-close"
                className="p-2 rounded-full text-[#6B7280] hover:bg-gray-100 hover:text-[#0A0B10] transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 pb-5 space-y-2.5">
              <div>
                <label className="block text-[11px] font-bold text-[#4B5563] mb-1 uppercase tracking-wide">Name</label>
                <input
                  ref={firstInputRef}
                  data-testid="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={handleChange('name')}
                  maxLength={100}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-[#4B5563] mb-1 uppercase tracking-wide">Email</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  maxLength={150}
                  required
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
                />
              </div>

              <div className="grid grid-cols-5 gap-2">
                <div className="col-span-2">
                  <label className="block text-[11px] font-bold text-[#4B5563] mb-1 uppercase tracking-wide">
                    Phone <span className="text-gray-400 normal-case">(opt)</span>
                  </label>
                  <input
                    data-testid="contact-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange('phone')}
                    maxLength={20}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
                  />
                </div>
                <div className="col-span-3">
                  <label className="block text-[11px] font-bold text-[#4B5563] mb-1 uppercase tracking-wide">Subject</label>
                  <select
                    data-testid="contact-subject-select"
                    value={form.subject}
                    onChange={handleChange('subject')}
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-2 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none"
                  >
                    {SUBJECT_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-[#4B5563] mb-1 uppercase tracking-wide">Message</label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={handleChange('message')}
                  maxLength={4000}
                  rows={4}
                  required
                  placeholder="How can we help?"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-[#0A0B10] focus:border-[#1800ad] focus:ring-2 focus:ring-[#1800ad]/10 outline-none resize-none"
                />
                <p className="text-[10px] text-gray-400 mt-1 text-right">{form.message.length}/4000</p>
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={submitting}
                className="w-full mt-1 inline-flex items-center justify-center gap-2 rounded-xl bg-[#1800ad] text-white text-sm font-bold py-2.5 hover:bg-[#0f0080] transition-colors disabled:opacity-60"
              >
                <Send size={16} />
                {submitting ? 'Sending…' : 'Send Message'}
              </button>
              <p className="text-[10px] text-center text-gray-400">Or email us directly at <a href="mailto:help@genzox.in" className="text-[#1800ad] font-semibold">help@genzox.in</a></p>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default FloatingContactFAB;
